/** Raw <restech> data (plan §3): what the reservation system holds for the booking NOW —
 *  one live, read-only request on demand (audited, ≤ 10 s, nothing imported) — beside the
 *  last webhook it sent, and the difference between the two.
 *
 *  Never fetched on its own: the operator's click (or the Actions menu's "View raw …
 *  data") asks, and the answer stays until the next click. A vendor failure is part of the
 *  answer (`live.ok: false`): the reason shows, the vendor's error body when it sent one,
 *  and the last webhook still does. A booking its restech cannot be asked for one at a
 *  time (`refresh.supported: false`) still shows its last webhook. The difference
 *  compares both bodies normalised — JSON with sorted keys, XML indented — so key order
 *  and whitespace do not read as changes. */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Alert, Box, Button, Link, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { BookingRecordRestech } from '@/api/bookingRecord';
import {
  useRestechRaw,
  type BookingRestechRaw,
  type BookingRestechRawError,
  type BookingRestechRawFetch,
  type BookingRestechWebhook,
} from '@/api/bookingActions';
import { ConsoleApiError } from '@/api/client';
import { DocumentViewer } from '@/components/DocumentViewer/DocumentViewer';
import { TextDiffView } from '@/features/notifications/TextDiffView';
import { consoleRouteFor } from '@/utils/gridLinks';
import { detectFormat, prettyPrint } from '@/utils/prettyPrint';
import { formatZoned } from '@/utils/zonedTime';

import { MONO_SX } from '../panels/displays';

import { TabSection } from './TabSection';
import { TabSkeleton } from './TabSkeleton';

export const RAW_SECTION_ID = 'booking-integration-raw';

type View = 'live' | 'webhook' | 'diff';

function liveFailure(e: BookingRestechRawError | null, name: string): string {
  switch (e) {
    case 'not_found':
      return `${name} has no such booking`;
    case 'vendor_error':
      return `${name} answered with an error`;
    case 'timeout':
      return `${name} did not answer within 10 seconds`;
    case 'not_supported':
      return `${name} bookings cannot be read one at a time`;
    case 'no_booking_id':
      return `the booking has no ${name} booking id to ask for`;
    case 'not_configured':
      return `the tenant’s ${name} integration cannot be used`;
    case 'not_managed':
      return 'no reservation system feeds this booking';
    default:
      return 'the read failed';
  }
}

function sortKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(sortKeys);
  if (v && typeof v === 'object') {
    return Object.fromEntries(
      Object.keys(v as Record<string, unknown>)
        .sort()
        .map((k) => [k, sortKeys((v as Record<string, unknown>)[k])]),
    );
  }
  return v;
}

/** The body as the difference reads it: JSON with sorted keys, XML indented, text as is. */
export function normalisedBody(text: string, hint?: string | null): string {
  const format = detectFormat(text, hint ?? undefined);
  if (format === 'json') {
    try {
      return JSON.stringify(sortKeys(JSON.parse(text)), null, 2);
    } catch {
      return text;
    }
  }
  return (format === 'xml' ? prettyPrint(text, 'xml') : null) ?? text;
}

function Note({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0, display: 'flex', gap: 0.75 }}>
      <InfoOutlinedIcon aria-hidden sx={{ fontSize: 16, mt: '2px', flex: 'none' }} />
      <span>{children}</span>
    </Typography>
  );
}

function WebhookCaption({ webhook, tz }: { webhook: BookingRestechWebhook; tz: string }) {
  const r = webhook.request;
  const href = consoleRouteFor('api_request', r.id);
  return (
    <Typography variant="body2" color="text.secondary" component="span">
      {href ? (
        <Link component={RouterLink} to={href} underline="hover">
          API request #{r.id}
        </Link>
      ) : (
        `API request #${r.id}`
      )}
      {` · ${formatZoned(r.created_ts, tz)}`}
      {r.name ? ` · ${r.name}` : ''}
    </Typography>
  );
}

/** A live answer under its toggle ("<restech> now"); a failure leads with the reason and
 *  labels the body as the vendor's error answer. */
function LiveView({ live, name, bookingId }: { live: BookingRestechRawFetch; name: string; bookingId: number }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1.5 }}>
      {!live.ok && (
        <Alert severity="warning" icon={<ErrorOutlineIcon fontSize="inherit" />}>
          No booking from {name}: {liveFailure(live.error, name)}.
          {live.error_detail ? ` ${live.error_detail}` : ''}
        </Alert>
      )}
      {live.body ? (
        <DocumentViewer
          text={live.body}
          {...(live.format ? { formatHint: live.format } : {})}
          downloadName={`booking-${bookingId}-${name.toLowerCase()}-live`}
          copyMessage="Live answer copied"
          emptyMessage="Empty answer."
          {...(live.ok
            ? {}
            : {
                leading: (
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {name}’s answer
                  </Typography>
                ),
              })}
          {...(live.truncated
            ? { beforeViewer: <Typography variant="caption" color="text.secondary">Cut at 1 MiB.</Typography> }
            : {})}
        />
      ) : (
        live.ok && <Note>{name} answered with an empty body.</Note>
      )}
    </Box>
  );
}

function WebhookView({
  webhook,
  name,
  tz,
  bookingId,
}: {
  webhook: BookingRestechWebhook | null;
  name: string;
  tz: string;
  bookingId: number;
}) {
  if (!webhook) return <Note>No webhook or API request from {name} is stored for this booking.</Note>;
  if (webhook.error === 'unavailable' || webhook.body === null) {
    return (
      <Box sx={{ display: 'grid', gap: 1 }}>
        <WebhookCaption webhook={webhook} tz={tz} />
        <Note>The stored body of this request could not be read.</Note>
      </Box>
    );
  }
  return (
    <DocumentViewer
      text={webhook.body}
      {...(webhook.format ? { formatHint: webhook.format } : {})}
      downloadName={`booking-${bookingId}-last-webhook`}
      copyMessage="Webhook body copied"
      emptyMessage="The stored body is empty."
      leading={<WebhookCaption webhook={webhook} tz={tz} />}
      {...(webhook.truncated
        ? { beforeViewer: <Typography variant="caption" color="text.secondary">Cut at 1 MiB.</Typography> }
        : {})}
    />
  );
}

function DiffView({ raw, name, tz }: { raw: BookingRestechRaw; name: string; tz: string }) {
  const before = raw.last_webhook?.body ?? '';
  const after = raw.live.body ?? '';
  const texts = useMemo(
    () => ({
      before: normalisedBody(before, raw.last_webhook?.format),
      after: normalisedBody(after, raw.live.format),
    }),
    [before, after, raw.last_webhook?.format, raw.live.format],
  );
  const same = texts.before === texts.after;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1 }}>
      <Typography variant="body2" color="text.secondary" component="p" sx={{ m: 0 }}>
        <Box component="span" sx={MONO_SX}>
          −
        </Box>{' '}
        the last webhook{raw.last_webhook ? ` (${formatZoned(raw.last_webhook.request.created_ts, tz)})` : ''} ·{' '}
        <Box component="span" sx={MONO_SX}>
          +
        </Box>{' '}
        {name} now ({formatZoned(raw.fetched_ts, tz)}) · keys sorted, whitespace normalised
      </Typography>
      {same ? (
        <Note>No difference: {name} holds what its last webhook sent.</Note>
      ) : (
        <TextDiffView before={texts.before} after={texts.after} maxHeight={480} />
      )}
    </Box>
  );
}

function requestFailure(e: unknown, name: string): string {
  if (e instanceof ConsoleApiError) {
    if (e.status === 429) return `Too many raw reads in the last minute — try again shortly. ${e.message}`;
    if (e.status === 403) return `Reading raw ${name} data needs the console_bookings_restech_raw permission.`;
    return e.message;
  }
  return e instanceof Error ? e.message : 'The read failed.';
}

export function RestechRawSection({
  bookingId,
  restech,
  timezone,
  fetchRequest,
}: {
  bookingId: number;
  restech: BookingRecordRestech;
  timezone: string;
  /** A new value asks for a live read now — the Actions menu's "View raw … data" (a read
   *  already on screen is kept). */
  fetchRequest?: number | null;
}) {
  const name = restech.managed_by_display ?? restech.managed_by ?? 'the booking system';
  const supported = restech.refresh.supported;
  const q = useRestechRaw(bookingId);
  const raw = q.data;
  const [view, setView] = useState<View | null>(null);

  // One read per request (the hook dedupes an in-flight read; each read is an audit row).
  const handled = useRef<number | null>(null);
  const { refetch } = q;
  const hasData = raw !== undefined;
  useEffect(() => {
    if (fetchRequest == null || handled.current === fetchRequest) return;
    handled.current = fetchRequest;
    if (!hasData) void refetch({ cancelRefetch: false });
  }, [fetchRequest, hasData, refetch]);

  const read = () => {
    setView(null);
    void refetch({ cancelRefetch: false });
  };
  const fetching = q.isFetching;
  const buttonLabel = fetching
    ? 'Reading…'
    : supported
      ? raw
        ? `Read ${name} again`
        : `Read live from ${name}`
      : raw
        ? 'Show again'
        : 'Show the last webhook';

  const canDiff = Boolean(raw?.live.ok && raw.live.body && raw.last_webhook?.body);
  const current: View = view ?? (raw?.live.ok ? 'live' : 'webhook');

  return (
    <TabSection
      id={RAW_SECTION_ID}
      title={`Raw ${name} data`}
      action={
        <Tooltip title="Audited · one live request, nothing is imported">
          <span>
            <Button size="small" variant="outlined" onClick={read} disabled={fetching} data-raw-read>
              {buttonLabel}
            </Button>
          </span>
        </Tooltip>
      }
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 1.5 }} aria-busy={fetching || undefined}>
        {!raw && !fetching && !q.error && (
          <Note>
            {supported
              ? `Reads the booking from ${name} now — one live request, audited, nothing is imported — and shows it beside the last webhook ${name} sent, with the difference.`
              : `${name} cannot be asked for one booking, so only the last webhook it sent can be shown.`}
          </Note>
        )}
        {fetching && <TabSkeleton label={`Reading the booking from ${name}`} rows={6} />}
        {!fetching && q.error && (
          <Alert severity="error">Nothing to show: {requestFailure(q.error, name)}</Alert>
        )}
        {!fetching && raw && (
          <>
            {raw.booking_id !== raw.requested_booking_id && (
              <Note>
                A party member: {name} holds its lead, booking{' '}
                <Link component={RouterLink} to={`/bookings/${raw.booking_id}/integration`} underline="hover">
                  #{raw.booking_id}
                </Link>
                .
              </Note>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={current}
                onChange={(_, v: View | null) => v && setView(v)}
                aria-label="Show"
              >
                <ToggleButton value="live" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                  {name} now
                </ToggleButton>
                <ToggleButton value="webhook" sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                  Last webhook
                </ToggleButton>
                <ToggleButton value="diff" disabled={!canDiff} sx={{ px: 1.5, py: 0.25, textTransform: 'none' }}>
                  Difference
                </ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {[
                  `Read ${formatZoned(raw.fetched_ts, timezone, { weekday: false })}`,
                  raw.live.vendor_booking_id ? `booking ${raw.live.vendor_booking_id}` : null,
                  raw.live.status_code != null ? `HTTP ${raw.live.status_code}` : null,
                  raw.live.duration_ms != null ? `${raw.live.duration_ms} ms` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Typography>
            </Box>
            {raw.live.request_url && current === 'live' && (
              <Typography variant="caption" color="text.secondary" sx={{ ...MONO_SX, fontSize: 12, wordBreak: 'break-all' }}>
                GET {raw.live.request_url}
              </Typography>
            )}
            {current === 'live' && <LiveView live={raw.live} name={name} bookingId={raw.booking_id} />}
            {current === 'webhook' && (
              <WebhookView webhook={raw.last_webhook} name={name} tz={timezone} bookingId={raw.booking_id} />
            )}
            {current === 'diff' && canDiff && <DiffView raw={raw} name={name} tz={timezone} />}
          </>
        )}
      </Box>
    </TabSection>
  );
}
